export type User = {
  id: string;
  name: string;
  email: string;
  currency: string;
  avatar?: string;
  color?: number;
  exchangeRates?: Record<string, number>;
};

export const SUPPORTED_CURRENCIES = ["AWG", "EUR", "USD"] as const;
export function currencyOptions(current?: string): string[] {
  return Array.from(
    new Set(
      [current?.toUpperCase(), ...SUPPORTED_CURRENCIES].filter(
        Boolean,
      ) as string[],
    ),
  );
}

export type Person = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
  color: number;
};

export type CreatePersonInput = {
  displayName: string;
  email?: string;
  phone?: string;
  avatar?: string;
};

export type Group = {
  id: string;
  name: string;
  description: string;
  memberIds: string[];
};

export type Expense = {
  id: string;
  description: string;
  amountCents: number;
  currency: string;
  groupId: string;
  paidById: string;
  participantIds: string[];
  occurredAt?: string;
  createdAt: string;
};

export type Settlement = {
  id: string;
  personId: string;
  amountCents: number;
  currency: string;
  direction: "paid" | "received";
  paidAt?: string;
  createdAt: string;
};

export type CurrencyExchange = {
  id: string;
  personId: string;
  fromAmountCents: number;
  fromCurrency: string;
  toAmountCents: number;
  toCurrency: string;
  rateBps: number;
  occurredAt?: string;
  createdAt: string;
};

export type Balance = {
  personId: string;
  amountCents: number;
  currency: string;
};

export type Overview = {
  currentUser: User;
  people: Person[];
  groups: Group[];
  expenses: Expense[];
  settlements: Settlement[];
  exchanges: CurrencyExchange[];
  balances: Balance[];
};

export type Loan = {
  id: string;
  lenderPersonId: string;
  borrowerPersonId: string;
  principalCents: number;
  currency: string;
  fee: { type: "fixed" | "percentage"; amountCents?: number; rateBps?: number };
  monthlyInterestBps: number;
  accrual: "daily";
  compound: boolean;
  issuedAt: string;
  dueAt?: string;
  status: "DRAFT" | "ACTIVE" | "OVERDUE" | "PAID" | "CANCELLED";
};

export type LoanBalance = {
  loanId: string;
  currency: string;
  principalOutstandingCents: number;
  feeOutstandingCents: number;
  interestOutstandingCents: number;
  outstandingCents: number;
};

export type LoanPayment = {
  id: string;
  loanId: string;
  amountCents: number;
  interestCents: number;
  feeCents: number;
  principalCents: number;
  paidAt: string;
  method?: string;
  notes?: string;
};

export type CreateLoanInput = {
  lenderPersonId: string;
  borrowerPersonId: string;
  principalCents: number;
  currency: string;
  feeType: "fixed" | "percentage";
  feeAmountCents?: number;
  feeRateBps?: number;
  monthlyInterestBps: number;
  accrual?: "daily";
  compound?: boolean;
  dueAt?: string;
};

export type CreateLoanPaymentInput = {
  amountCents: number;
  method?: string;
  notes?: string;
};

export type AuthResponse = {
  token: string;
  user: {
    id: string;
    personId: string;
    email: string;
    defaultCurrency: string;
    status: string;
  };
};

export const AUTH_TOKEN_KEY = "settleup.token";
export const AUTH_ACTIVITY_KEY = "settleup.lastActivityAt";

export type DatabaseSyncPhase =
  | "PREPARING"
  | "DOWNLOADING"
  | "VERIFYING"
  | "APPLYING"
  | "COMPLETED"
  | "FAILED";

export type DatabaseSyncJob = {
  id: string;
  status: DatabaseSyncPhase;
  phase: DatabaseSyncPhase;
  percent: number;
  sourceEnvironment: "production";
  targetEnvironment: "development" | "staging";
  downloadedBytes: number;
  totalBytes?: number | null;
  snapshotGeneratedAt?: string;
  startedAt: string;
  completedAt?: string;
  checksum?: string;
  error?: string;
  message: string;
};

export type DatabaseSyncState = {
  environment: string;
  canSyncFromProduction: boolean;
  activeJob?: DatabaseSyncJob | null;
  lastJob?: DatabaseSyncJob | null;
};

export type CreateGroupInput = {
  name: string;
  description: string;
  memberIds: string[];
};

export type CreateExpenseInput = {
  description: string;
  amountCents: number;
  currency: string;
  occurredAt?: string;
  groupId?: string;
  paidById: string;
  participantIds: string[];
  splitType?: "equal" | "exact" | "percentage" | "shares" | "items";
  splits?: Array<{
    personId: string;
    amountCents?: number;
    percentage?: number;
    shares?: number;
  }>;
};

export type UpdateExpenseInput = {
  description: string;
  amountCents: number;
  currency: string;
  occurredAt?: string;
};

export type CreateSettlementInput = {
  personId: string;
  amountCents: number;
  currency: string;
  direction: "paid" | "received";
};

export type CreateCurrencyExchangeInput = {
  personId: string;
  fromAmountCents: number;
  fromCurrency: string;
  toAmountCents: number;
  toCurrency: string;
  rateBps: number;
};

export type ProfileSettingsInput = {
  email: string;
  currency: string;
  avatar: string;
  exchangeRates: Record<string, number>;
};

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
};

type APIError = {
  error?: string | { message?: string };
};

export function getStoredAuthToken() {
  return typeof window !== "undefined"
    ? window.localStorage.getItem(AUTH_TOKEN_KEY)
    : null;
}

export function storeAuthSession(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  window.localStorage.setItem(AUTH_ACTIVITY_KEY, String(Date.now()));
}

export function storeAuthToken(token: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthSession() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_ACTIVITY_KEY);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getStoredAuthToken();
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  const payload = (await response.json().catch(() => ({}))) as T & APIError;
  if (!response.ok) {
    if (
      response.status === 401 &&
      typeof window !== "undefined" &&
      path.startsWith("/api/v1")
    ) {
      window.dispatchEvent(new Event("settleup:auth-expired"));
    }
    const message =
      typeof payload.error === "string"
        ? payload.error
        : payload.error?.message;
    throw new Error(message ?? "Something went wrong. Please try again.");
  }
  return payload;
}

export const ledgerAPI = {
  getOverview: async () => {
    const overview = await request<Overview>("/api/overview");
    return {
      ...overview,
      people: overview.people ?? [],
      groups: overview.groups ?? [],
      expenses: overview.expenses ?? [],
      settlements: overview.settlements ?? [],
      exchanges: overview.exchanges ?? [],
      balances: overview.balances ?? [],
    };
  },
  createGroup: (input: CreateGroupInput) =>
    request<Overview>("/api/groups", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createExpense: (input: CreateExpenseInput) =>
    request<Overview>("/api/expenses", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createSettlement: (input: CreateSettlementInput) =>
    request<Overview>("/api/settlements", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createCurrencyExchange: (input: CreateCurrencyExchangeInput) =>
    request<Overview>("/api/exchanges", {
      method: "POST",
      body: JSON.stringify(input),
    }),
};

export const v1API = {
  login: (email: string, password: string) =>
    request<AuthResponse>("/api/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  register: (
    email: string,
    password: string,
    displayName: string,
    defaultCurrency = "AWG",
  ) =>
    request<AuthResponse>("/api/v1/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, displayName, defaultCurrency }),
    }),
  refresh: () =>
    request<AuthResponse>("/api/v1/auth/refresh", {
      method: "POST",
      body: "{}",
    }),
  getDatabaseSyncState: () =>
    request<DatabaseSyncState>("/api/v1/system/database-sync"),
  startDatabaseSync: () =>
    request<DatabaseSyncJob>("/api/v1/system/database-sync", {
      method: "POST",
      body: "{}",
    }),
  getDatabaseSync: (syncID: string) =>
    request<DatabaseSyncJob>(
      `/api/v1/system/database-sync/${encodeURIComponent(syncID)}`,
    ),
  createPerson: (input: CreatePersonInput) =>
    request("/api/v1/people", {
      method: "POST",
      headers: { "Idempotency-Key": `person-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
  updatePerson: (personID: string, input: CreatePersonInput) =>
    request(`/api/v1/people/${encodeURIComponent(personID)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  updateExpense: (expenseID: string, input: UpdateExpenseInput) =>
    request<Expense>(`/api/v1/expenses/${encodeURIComponent(expenseID)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  updateMe: (input: ProfileSettingsInput) =>
    request<User>('/api/v1/me', {
      method: 'PATCH',
      body: JSON.stringify({
        email: input.email,
        defaultCurrency: input.currency,
        exchangeRates: input.exchangeRates,
      }),
    }),
  updatePassword: (input: ChangePasswordInput) =>
    request<void>('/api/v1/me/password', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  listLoans: () => request<Loan[]>("/api/v1/loans"),
  createLoan: (input: CreateLoanInput) =>
    request<Loan>("/api/v1/loans", {
      method: "POST",
      headers: { "Idempotency-Key": `loan-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
  getLoanBalance: (loanID: string) =>
    request<LoanBalance>(`/api/v1/loans/${loanID}/balance`),
  listLoanPayments: (loanID: string) =>
    request<LoanPayment[]>(`/api/v1/loans/${loanID}/payments`),
  createLoanPayment: (loanID: string, input: CreateLoanPaymentInput) =>
    request<LoanPayment>(`/api/v1/loans/${loanID}/payments`, {
      method: "POST",
      headers: { "Idempotency-Key": `loan-payment-${crypto.randomUUID()}` },
      body: JSON.stringify(input),
    }),
};

export function findPerson(
  data: Overview,
  id: string,
): Person | User | undefined {
  if (id === data.currentUser.id) {
    return data.currentUser;
  }
  return data.people.find((person) => person.id === id);
}

export function findGroup(data: Overview, id: string): Group | undefined {
  return data.groups.find((group) => group.id === id);
}

export function formatMoney(amountCents: number, currency: string): string {
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

export function centsToInput(amountCents: number): string {
  return (amountCents / 100).toFixed(2);
}

export function parseCents(value: string): number | null {
  const match = value.trim().match(/^(\d+)(?:[.,](\d{1,2}))?$/);
  if (!match) {
    return null;
  }

  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(fraction)) {
    return null;
  }

  return whole * 100 + fraction;
}

export function equalShares(amountCents: number, count: number): number[] {
  if (count < 1) {
    return [];
  }
  const share = Math.floor(amountCents / count);
  const remainder = amountCents % count;
  return Array.from(
    { length: count },
    (_, index) => share + (index < remainder ? 1 : 0),
  );
}

export function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

export function formatActivityDate(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  return formatShortDate(value);
}

export function groupSpent(data: Overview, groupID: string): number {
  return data.expenses
    .filter((expense) => expense.groupId === groupID)
    .reduce((total, expense) => total + expense.amountCents, 0);
}

export function groupBalanceByPerson(
  data: Overview,
  groupID: string,
): Map<string, number> {
  const balances = new Map<string, number>();
  const expenses = data.expenses.filter(
    (expense) => expense.groupId === groupID,
  );

  expenses.forEach((expense) => {
    const shares = equalShares(
      expense.amountCents,
      expense.participantIds.length,
    );
    expense.participantIds.forEach((participantID, index) => {
      const share = shares[index];
      if (
        expense.paidById === data.currentUser.id &&
        participantID !== data.currentUser.id
      ) {
        balances.set(participantID, (balances.get(participantID) ?? 0) + share);
      }
      if (
        participantID === data.currentUser.id &&
        expense.paidById !== data.currentUser.id
      ) {
        balances.set(
          expense.paidById,
          (balances.get(expense.paidById) ?? 0) - share,
        );
      }
    });
  });

  return balances;
}
